import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";

import {
  copyText,
  createShare,
  EXPIRY_OPTIONS,
  Share,
  shareUrl,
} from "./app/shares";

function ShareDialog({
  open,
  fileKey,
  isFolder,
  onClose,
}: {
  open: boolean;
  fileKey: string | null;
  isFolder: boolean;
  onClose: () => void;
}) {
  const [oneTime, setOneTime] = useState(false);
  const [expiryIndex, setExpiryIndex] = useState(2); // 7 days
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<Share | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Start each new share from a clean slate.
  useEffect(() => {
    if (!open) return;
    setCreated(null);
    setError(null);
    setCopied(false);
    setCreating(false);
  }, [open, fileKey]);

  const url = created ? created.url ?? shareUrl(created.id) : "";

  async function handleCreate() {
    if (!fileKey) return;
    setCreating(true);
    setError(null);
    try {
      const share = await createShare({
        key: fileKey,
        // Browsing a folder would burn a one-time link on the first page view.
        oneTime: isFolder ? false : oneTime,
        expiresIn: EXPIRY_OPTIONS[expiryIndex].value,
      });
      setCreated(share);
    } catch (e: any) {
      setError(e?.message ?? "Could not create link");
    } finally {
      setCreating(false);
    }
  }

  async function handleCopy() {
    if (await copyText(url)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  const filename = fileKey?.split("/").pop() ?? "";
  const isMedia = /\.(mp4|m4v|mov|webm|mkv|avi|m4a|mp3|aac|flac|ogg|opus|wav)$/i.test(
    filename
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>
        {isFolder ? "Share folder" : "Share link"}
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          title={filename}
          sx={{ mb: 2 }}
        >
          {filename}
        </Typography>

        {created ? (
          <Stack spacing={1.5}>
            <Alert severity="success" sx={{ py: 0.5 }}>
              Link created
              {created.oneTime
                ? " — it works exactly once."
                : isFolder
                ? " — anyone with it can browse this folder."
                : "."}
            </Alert>
            <TextField
              value={url}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              onFocus={(e) => e.target.select()}
            />
            <Button
              variant="contained"
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopy}
            >
              {copied ? "Copied" : "Copy link"}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Anyone with this link can {isFolder ? "browse and download" : "download"}{" "}
              without signing in. You can turn it off at any time from the
              admin panel.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {error && (
              <Alert severity="error" sx={{ py: 0.5 }}>
                {error}
              </Alert>
            )}
            {isFolder ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Recipients can browse this folder and download anything in it,
                without signing in. One-time links are not available for
                folders — the first page view would use it up.
              </Alert>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    checked={oneTime}
                    onChange={(e) => setOneTime(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">One-time link</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Stops working after the first download
                    </Typography>
                  </Box>
                }
              />
            )}

            {!isFolder && isMedia && oneTime && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                A one-time link cannot be streamed. Players request a file in
                pieces, and the link is used up by the first piece — this link
                will download the whole file instead of playing it.
              </Alert>
            )}

            {!isFolder && isMedia && !oneTime && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Opening this link plays the video in the browser, with seeking.
                The same URL also works as a direct stream link in VLC.
              </Alert>
            )}

            <FormControl fullWidth size="small">
              <InputLabel id="share-expiry-label">Expires</InputLabel>
              <Select
                labelId="share-expiry-label"
                label="Expires"
                value={expiryIndex}
                onChange={(e) => setExpiryIndex(Number(e.target.value))}
              >
                {EXPIRY_OPTIONS.map((option, index) => (
                  <MenuItem key={option.label} value={index}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{created ? "Done" : "Cancel"}</Button>
        {!created && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !fileKey}
          >
            {creating ? "Creating…" : "Create link"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ShareDialog;
